import * as _ from 'lodash';
import * as gtinUtils from 'gtin';
import {ConfigProps, FormErrors} from 'redux-form';
import {ExtendedWrappedFieldExternalProps} from '../FormFields';
import {FieldInfo} from '../FormSections/consts';
import isISBN from 'validator/lib/isISBN';
import memoize from 'memoize-one';
import {Map} from 'immutable';
import {getSiblings, isChildRow} from 'cl-table/VirtualizedHelpers';
export type ValueType = string | number | object[];

export interface ValidationFunction<FormData extends {} = {}> {
  (value: ValueType | File, formData: FormData, data: Map<number, any>): string;
}

export interface Validator<FormData extends {} = {}> {
  (fieldName: string): ValidationFunction<FormData>;
}

export interface CreateValidator<FormData extends {} = {}> {
  (...args: any): Validator<FormData>;
}

export interface ValidatorCondition<FormData extends {} = {}> {
  (formData: FormData): boolean;
}

export function createValidateAllObjectRequired(fields): ConfigProps['validate'] {
  return values => {
    return _.pickBy<FormErrors>(
      _.mapValues(_.keyBy(fields, 'name'), (fieldInfo, columnsName) => {
        let value = values[columnsName];
        if (!_.isEmpty(value)) {
          value = _.values(JSON.parse(value))[0];
          return nonEmpty('At least one option value')(value, values, undefined);
        }
      })
    ) as FormErrors;
  };
}

export function createValidateRequired<FormData extends object = {}>(
  fields: Array<ExtendedWrappedFieldExternalProps<keyof FormData>>
): ConfigProps<FormData>['validate'] {
  return values => {
    return _.pickBy<FormErrors>(
      _.mapValues(_.keyBy(fields, 'name'), (fieldInfo, columnsName) => {
        const value = values[columnsName];
        return nonEmpty('This field')(value, values, undefined);
      })
    ) as FormErrors;
  };
}

export function createValidateTypedFields<FormData extends object = {}>(
  fields: Array<FieldInfo<FormData>>
): ConfigProps<FormData>['validate'] {
  return values => {
    if (values) {
      return _.pickBy<FormErrors>(
        _.mapValues(_.keyBy(fields, 'name'), (fieldInfo, columnsName) => {
          const value = values[columnsName];
          return getTypedFieldValidationFunction(columnsName, fieldInfo)(value, values, undefined);
        })
      ) as FormErrors;
    }
    return {};
  };
}

const createOptionsValidator: CreateValidator = (options: FieldInfo['options']) => fieldName => {
  const optionsByValue = _.keyBy(options, 'value');
  return (value: number | string) => {
    if (!isEmpty(value) && !optionsByValue[value]) {
      return `${value} is not a valid ${fieldName}`;
    }
  };
};

const getTypedFieldValidationFunctionCache = {};

export const getTypedFieldValidationFunction = (columnName, fieldInfo: FieldInfo): ValidationFunction => {
  if (!getTypedFieldValidationFunctionCache[columnName]) {
    getTypedFieldValidationFunctionCache[columnName] = memoize((columnName, fieldInfo: FieldInfo) => {
      const {
        type,
        validator,
        displayName,
        maxLength,
        minLength,
        maxValue,
        minValue,
        valueFormatter,
        formatErrorMessage,
        options,
        creatable,
      } = fieldInfo;
      const validators = [];
      if (validator) {
        validators.push(validator);
      }
      switch (type) {
        case 'shortString':
          if (_.isFinite(maxLength) || _.isFinite(minLength)) {
            if (maxLength === minLength) {
              validators.push(createLengthValidator(null, null, maxLength));
            } else {
              validators.push(createLengthValidator(maxLength, minLength));
            }
          }
          break;
        case 'integer':
        case 'float':
          validators.push(createNumberValidator());
          if (_.isFinite(maxValue) || _.isFinite(minValue)) {
            validators.push(createNumericalValueBoundaryValidator(maxValue, minValue));
          }
          break;
        case 'select':
          if (!creatable) {
            validators.push(createOptionsValidator(options));
          }
          break;
      }
      if (valueFormatter) {
        validators.push(createValueFormatValidator(valueFormatter, formatErrorMessage));
      }
      return compose(...validators)(displayName ? displayName : columnName);
    });
  }
  return getTypedFieldValidationFunctionCache[columnName](columnName, fieldInfo);
};

export const isEmpty = value => {
  return _.isEmpty(value) && !_.isNumber(value) && !_.isBoolean(value) && !(value instanceof File);
};

export const nonEmpty: Validator = fieldName => value => {
  if (isEmpty(value)) {
    return `${fieldName} is required`;
  }
};

export const positive: Validator = fieldName => value => {
  if (!_.isNil(value) && value <= 0) {
    return `${fieldName} needs to be greater than zero`;
  }
};

const createNumberValidator: CreateValidator = () => fieldName => (value: number | string) => {
  if (!isEmpty(value)) {
    const floatValue = _.isString(value) ? parseFloat(value) : value;
    if (_.isNaN(floatValue)) {
      return `${fieldName} must be a number`;
    }
  }
};

const createNumericalValueBoundaryValidator: CreateValidator = (maxValue?: number, minValue?: number) => fieldName => (
  value: number | string
) => {
  const floatValue = _.isString(value) ? parseFloat(value) : value;
  if (_.isFinite(maxValue) && _.isFinite(floatValue) && floatValue > maxValue) {
    return `${fieldName} can't be greater than ${maxValue}`;
  }
  if (_.isFinite(minValue) && _.isFinite(floatValue) && floatValue < minValue) {
    return `${fieldName} can't be lower than ${minValue}`;
  }
};

export const createLengthValidator: CreateValidator = (
  maxLength?: number,
  minLength?: number,
  exactLength?: number
) => fieldName => (value: string) => {
  if (exactLength && value && value.length !== exactLength) {
    return `${fieldName} must be exactly ${exactLength} characters long`;
  }
  if (maxLength && value && value.length > maxLength) {
    return `${fieldName} can't be more than ${maxLength} characters`;
  }
  if (minLength && value && value.length < minLength) {
    return `${fieldName} can't be less than ${minLength} characters`;
  }
};

export const createNonEmptyBySiblingsValidator: CreateValidator = (fieldNameToCompare?: string) => fieldName => (
  value: string,
  rowData: any,
  data?: Map<number, any>
) => {
  if (
    isChildRow(rowData) &&
    isEmpty(value) &&
    _.some(
      getSiblings(rowData, data),
      sibling => sibling && !isEmpty(sibling[fieldNameToCompare ? fieldNameToCompare : fieldName])
    )
  ) {
    return `${fieldName} is required`;
  }
};

export const createConditionalValidator: CreateValidator = (
  condition: ValidatorCondition,
  validator: Validator
) => fieldName => (value, formData, data) => {
  if (condition(formData)) {
    return validator(fieldName)(value, formData, data);
  }
};

export const createValueFormatValidator: CreateValidator = (regExp: string, errorMessage?: string) => fieldName => (
  value: string
) => {
  if (value && !new RegExp(regExp).test(value)) {
    return errorMessage ? errorMessage : `Invalid ${fieldName}`;
  }
};

export const gtin: Validator = fieldName => value => {
  if (_.isEmpty(value)) {
    // the gtinUtils util throw exception for empty value.
    return null;
  }
  if (!(_.isString(value) && ((gtinUtils.isGTIN(value) && gtinUtils.validate(value)) || isISBN(value)))) {
    return `This doesn't seem to be a valid ${fieldName}`;
  }
};

export const createConstantValueValidator: CreateValidator = (constant: ValueType) => fieldName => (
  value: ValueType
) => {
  if (value !== constant) {
    return `Invalid ${fieldName}`;
  }
};

export const createOrValidator: CreateValidator = (...validators: Array<Validator>): Validator => {
  return fieldName => {
    const validationFunctions: ValidationFunction[] = _.map(validators, validator => validator(fieldName));
    return (value, formData, data) => {
      let result: ReturnType<ValidationFunction> = null;
      _.forEach(validationFunctions, validationFunction => {
        if (validationFunction) {
          result = validationFunction(value, formData, data);
          if (!result) {
            return false;
          }
        }
      });
      return result;
    };
  };
};

export const compose: CreateValidator = (...validators: Array<Validator>): Validator => {
  return fieldName => {
    const validationFunctions: ValidationFunction[] = _.map(validators, validator => validator(fieldName));
    return (value, formData, data) => {
      let result: ReturnType<ValidationFunction> = null;
      _.forEach(validationFunctions, validationFunction => {
        if (validationFunction) {
          result = validationFunction(value, formData, data);
          if (result) {
            return false;
          }
        }
      });
      return result;
    };
  };
};
